import { Dialog } from '@angular/cdk/dialog';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { Component, computed, effect, inject, signal, untracked, ViewChild } from '@angular/core';
import { RouterLink } from '@angular/router';
import { OiqUiDropdownComponent } from '@oiq-ui-sdk/ui';
import { EmailListItemSkeletonComponent } from '@v2/modules/email-app/components/email-list-item-skeleton/email-list-item-skeleton.component';
import { EmailListItemComponent } from '@v2/modules/email-app/components/email-list-item/email-list-item.component';
import { EmailProviderSelectDialogComponent } from '@v2/modules/email-app/components/email-provider-select-dialog/email-provider-select-dialog.component';
import { EmailProviderSelectComponent } from '@v2/modules/email-app/components/email-provider-select/email-provider-select.component';
import { EmailProvidersSwitchComponent } from '@v2/modules/email-app/components/email-providers-switch/email-providers-switch.component';
import { GmailImapDialogComponent } from '@v2/modules/email-app/components/gmail-imap-dialog/gmail-imap-dialog.component';
import { EMAIL_APP_STORE, Label } from '@v2/modules/email-app/email-app.store';
import { EmailProvider } from '@v2/modules/email-app/services/email-oauth.service';

@Component({
  selector: 'oiq-email-button',
  imports: [
    OiqUiDropdownComponent,
    EmailListItemComponent,
    EmailListItemSkeletonComponent,
    RouterLink,
    EmailProvidersSwitchComponent,
    EmailProviderSelectComponent,
  ],
  templateUrl: './email-button.component.html',
  styleUrl: './email-button.component.scss',
})
export class EmailButtonComponent {
  private store = inject(EMAIL_APP_STORE);
  private dialog = inject(Dialog);
  @ViewChild('dropdownRef')
  dropdownRef: OiqUiDropdownComponent;

  public overlayPositions: ConnectedPosition[] = [
    {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'center',
      overlayY: 'top',
    },
  ];

  // Expose store signals
  isOAuthInProgress = this.store.isOAuthInProgress;
  oauthProvider = this.store.oauthProvider;
  emails = this.store.emails;
  isLoading = this.store.isLoading;
  hasEmails = this.store.hasEmails;
  isAuthenticated = this.store.isAuthenticated;
  authenticatedProviders = this.store.authenticatedProviders;
  labels = this.store.labels;

  // Active tab tracking - defaults to 'inbox' label id
  activeTab = signal<string | null>(null);

  // Track last known labels to detect provider changes
  private lastLabelsIds = signal<string>('');

  // Flag to prevent effect from resetting when user manually clicks a tab
  private isManualTabChange = false;

  // Filter labels to show only Inbox and Sent, always with Inbox first
  inboxAndSentLabels = computed(() => {
    const allLabels = this.labels();

    // Find inbox and sent labels from the API
    const inboxLabel = allLabels.find(label => label.name.toLowerCase() === 'inbox');
    const sentLabel = allLabels.find(label => label.name.toLowerCase() === 'sent');

    // Create synthetic Inbox label if it doesn't exist (e.g., for Outlook)
    // Use 'inbox' as the id since that's what the backend expects
    const syntheticInbox: Label = {
      id: 'inbox',
      name: 'Inbox',
    };

    const result: Label[] = [];

    // Always add Inbox first
    if (inboxLabel) {
      result.push(inboxLabel);
    } else {
      // Add synthetic Inbox if not in labels (e.g., Outlook)
      result.push(syntheticInbox);
    }

    // Add Sent if it exists
    if (sentLabel) {
      result.push(sentLabel);
    }

    return result;
  });

  // Computed property for provider display name
  providerDisplayName = computed(() => {
    const provider = this.oauthProvider();
    if (!provider) return '';
    if (provider === 'gmail' || provider === 'gmail_imap') return 'Gmail';
    return 'Outlook';
  });

  // Get first 4 emails
  displayEmails = computed(() => this.emails().slice(0, 4));

  // Ghost loader items count (4 items to match max displayEmails)
  skeletonItems = Array(4).fill(0);

  constructor() {
    // Set default active tab to inbox when labels are loaded or provider changes
    effect(() => {
      const labels = this.inboxAndSentLabels();
      const currentProvider = this.store.currentProvider();

      // Create a hash to detect when labels actually change (provider change)
      const currentLabelsIds = labels.map(l => l.id).join(',');
      const lastLabelsIds = untracked(() => this.lastLabelsIds());
      const labelsChanged = currentLabelsIds !== lastLabelsIds;

      // Skip if this is a manual tab change (user clicked a tab)
      if (untracked(() => this.isManualTabChange)) {
        untracked(() => {
          this.isManualTabChange = false;
        });
        return;
      }

      // Get current active tab without tracking it (to prevent infinite loop)
      const currentActiveTab = untracked(() => this.activeTab());

      // Update the last known labels if they changed (without tracking)
      if (labelsChanged) {
        untracked(() => {
          this.lastLabelsIds.set(currentLabelsIds);
        });
      }

      // Reset active tab when provider changes (labels changed) or no active tab is set
      if (currentProvider && labels.length > 0) {
        // Inbox is always first in the array
        const inboxLabel = labels[0];
        if (inboxLabel && inboxLabel.name.toLowerCase() === 'inbox') {
          // Only reset to inbox if:
          // 1. Labels changed (provider switch), OR
          // 2. Active tab is not set, OR
          // 3. Current active tab is not a valid label anymore
          const isValidLabel = labels.some(l => l.id === currentActiveTab);

          if (labelsChanged || !currentActiveTab || !isValidLabel) {
            untracked(() => {
              this.activeTab.set(inboxLabel.id);
              this.store.setEmailFilters({ labels: inboxLabel.id, pageToken: null });
            });
          }
        }
      } else {
        // Reset active tab when no provider or no labels
        untracked(() => {
          this.activeTab.set(null);
          this.lastLabelsIds.set('');
        });
      }
    });
  }

  onButtonClick() {
    this.store.initialize();
    this.dropdownRef.toggle();
  }

  onTabClick(label: Label) {
    // Set flag to prevent effect from resetting to inbox
    this.isManualTabChange = true;
    this.activeTab.set(label.id);
    const filters = { labels: label.id, pageToken: null };
    this.store.setEmailFilters(filters);
  }

  onRefreshEmails() {
    this.store.reloadEmails();
  }

  onAuth(provider?: EmailProvider) {
    // Use the provided provider, or fall back to current provider, or default to first authenticated provider
    const targetProvider =
      provider || this.store.currentProvider() || this.authenticatedProviders()[0];

    if (targetProvider === 'gmail_imap') {
      this.openGmailImapDialog();
      return;
    }

    if (targetProvider) {
      this.store.startOAuthFlow(targetProvider);
    } else {
      console.error('No provider available for OAuth flow');
    }
  }

  onAddMailbox(): void {
    this.dropdownRef.close();
    this.dialog.open(EmailProviderSelectDialogComponent, {
      autoFocus: false,
    });
  }

  private openGmailImapDialog(): void {
    this.dropdownRef.close();
    GmailImapDialogComponent.open(this.dialog);
  }
}
